import { ChangeEventHandler, useCallback, useMemo, useState } from 'react'

import { BillingAddress as BillingAddressType, CountryCode } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/image'

import CreatorsIcon from '@/assets/images/icons/creatorboost.png'
import CollapsibleContainer from '@/components/CollapsibleContainer'
import { useAppSelector } from '@/utils/useAppSelector'

import { useCustomerAddBillingAddressMutation, useLazyCustomerRemoveBillingAddressQuery } from '../../api'
import { Button } from '../../components/buttons/Button'
import { ConfirmButton } from '../../components/buttons/ConfirmButton'
import { TrashBin } from '../../components/Icons'
import { Arrow } from '../../components/Icons/Arrow'
import { SuccessIconAnimated } from '../../components/Icons/Success'

import styles from './BillingAddress.module.scss'

const defaultFormData: BillingAddressType = {
  country: '',
  address: '',
  postalCode: '',
  city: '',
  stateOrProvince: '',
}

const TextField = ({
  value,
  onChange,
  label,
  name,
  edit,
}: {
  name: string
  label: string
  value?: string
  onChange: ChangeEventHandler<HTMLInputElement>
  edit: boolean
}) => {
  return (
    <div className={cx(styles.Input, { [styles.Disabled]: !edit })}>
      <label className={styles.Label} htmlFor={name}>
        {label}
      </label>
      <input name={name} disabled={!edit} className={styles.TextField} value={value} onChange={onChange} type="text" />
    </div>
  )
}

const SelectField = ({
  name,
  label,
  value,
  options,
  onChange,
  edit,
}: {
  name: string
  label: string
  value?: string
  error?: boolean
  onChange: ChangeEventHandler<HTMLSelectElement>
  edit: boolean
  options: { value: string; label: string }[]
}) => {
  return (
    <div className={cx(styles.Input, { [styles.Disabled]: !edit })}>
      <label className={styles.Label} htmlFor={name}>
        {label}
      </label>
      <div className={styles.SelectWrapper}>
        <Arrow className={styles.SelectArrow} direction="Down" />
        <select value={value} onChange={onChange} disabled={!edit}>
          <option value="" disabled>
            -
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

export const BillingAddress: React.FC<{ countryCodes: CountryCode[] }> = ({ countryCodes }) => {
  const billingAddress = useAppSelector((state) => state.user.profile?.billingAddress)
  const [edit, setEdit] = useState(!billingAddress)
  const t = useTranslations()

  const [isSubmitting, setSubmitting] = useState(false)
  const [addBillingAddress] = useCustomerAddBillingAddressMutation()
  const [removeBillingAddress] = useLazyCustomerRemoveBillingAddressQuery()
  const [formData, setFormData] = useState<BillingAddressType>(billingAddress ?? { ...defaultFormData })

  const updateFormData = useCallback(
    (value: Partial<BillingAddressType>) => {
      setFormData((prev) => {
        return { ...prev, ...value }
      })
    },
    [setFormData],
  )

  const countries = useTranslations('Countries')
  const sortedOptions = useMemo(
    () =>
      countryCodes
        .map((value) => ({
          value: countries(value.toLowerCase() as keyof IntlMessages['Countries']),
          label: countries(value.toLowerCase() as keyof IntlMessages['Countries']),
          id: value,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [countryCodes, countries],
  )

  const saveAddress = async () => {
    if (!formData.country) return
    setSubmitting(true)
    await addBillingAddress(formData)
    setSubmitting(false)
    setEdit(false)
  }

  const deleteAddress = async () => {
    await removeBillingAddress()
    setFormData(defaultFormData)
  }

  const saveButtonContent = isSubmitting ? (
    <SuccessIconAnimated height={28} width={28} className={styles.SuccessCheckmark} />
  ) : (
    t('acc_address_save')
  )

  const title = (
    <div className={styles.ContainerTitleRow}>
      <h2>{t('acc_address_title')}</h2>
      <div className={styles.CreatorsIcon}>
        <Image src={CreatorsIcon} height={25} objectFit="contain" alt="" />
      </div>
    </div>
  )

  const isEmpty = !billingAddress

  return (
    <CollapsibleContainer className={styles.ContentContainer} title={title}>
      <div className={styles.Form}>
        <SelectField
          name="country"
          label={t('acc_address_country')}
          value={formData.country}
          onChange={(e) => updateFormData({ country: e.currentTarget.value })}
          edit={edit}
          options={sortedOptions}
        />
        <TextField
          name="address"
          label={t('acc_address_street')}
          value={formData.address}
          onChange={(e) => updateFormData({ address: e.currentTarget.value })}
          edit={edit}
        />
        <div className={styles.InputGroup}>
          <TextField
            name="postalCode"
            label={t('acc_address_postal_code')}
            value={formData.postalCode}
            onChange={(e) => updateFormData({ postalCode: e.currentTarget.value })}
            edit={edit}
          />
          <TextField
            name="city"
            label={t('acc_address_city')}
            value={formData.city}
            onChange={(e) => updateFormData({ city: e.currentTarget.value })}
            edit={edit}
          />
        </div>
        <TextField
          name="stateOrProvince"
          label={t('acc_address_state')}
          value={formData.stateOrProvince}
          onChange={(e) => updateFormData({ stateOrProvince: e.currentTarget.value })}
          edit={edit}
        />
        {/** TODO Replace this when copies have been translated to all languages */}
        <p className={styles.Description}>
          This information will be shown on the purchase email receipt. Depending on your country, the payment processor
          may ask your name during the payment flow. In this case the submitted name will be displayed instead.
        </p>
        <div className={cx(styles.InputGroup, styles.Actions, { [styles.Empty]: isEmpty })}>
          {edit ? (
            <>
              <Button
                additionalClasses={cx(styles.Save, styles.Cancel)}
                onClick={() => setEdit(false)}
                type="OutlineSecondary"
              >
                {t('acc_address_cancel')}
              </Button>
              <Button additionalClasses={styles.Save} onClick={saveAddress}>
                {saveButtonContent}
              </Button>
            </>
          ) : (
            <>
              <ConfirmButton
                confirmText={t('acc_address_delete')}
                cancelText={t('acc_address_cancel')}
                titleText={t('acc_address_delete_title')}
                descriptionText={t('acc_address_delete_desc')}
                onConfirm={() => deleteAddress()}
                confirmButtonClass={styles.ConfirmDelete}
              >
                <Button additionalClasses={styles.Delete} type="OutlineSecondary">
                  <TrashBin />
                </Button>
              </ConfirmButton>
              <Button additionalClasses={styles.Save} onClick={() => setEdit(true)}>
                {t('acc_address_edit')}
              </Button>
            </>
          )}
        </div>
      </div>
    </CollapsibleContainer>
  )
}
